import { useEffect, useState } from 'react';
import { sportsVodApi } from '@/services/sportsVodApi';
import type { FfrServer } from '@/types/ffr';
import type { SportsVodSource } from '@/types/sportsVod';

export type FfrServersStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export interface UseFfrServersResult {
  servers: FfrServer[] | null;
  status: FfrServersStatus;
}

/**
 * Fetches the multiserver tree for a fullfightreplays title. For any other
 * source it stays 'idle' and never hits the network. 'unavailable' (404) means
 * the backend flag is off / not an FFR title — the caller renders the legacy
 * single-embed player. 'error' means the endpoint exists but failed (outage).
 */
export function useFfrServers(id: string, source: SportsVodSource | null): UseFfrServersResult {
  const [servers, setServers] = useState<FfrServer[] | null>(null);
  const [status, setStatus] = useState<FfrServersStatus>('idle');

  useEffect(() => {
    if (source !== 'fullfightreplays') {
      setServers(null);
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    setServers(null);
    sportsVodApi
      .getFfrServers(id, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result === null) { setStatus('unavailable'); return; }
        setServers(result);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err as Error)?.name === 'AbortError') return;
        setStatus('error');
      });
    return (): void => controller.abort();
  }, [id, source]);

  return { servers, status };
}
